import assertPayload from "./assert-payload";
import { domComponentPath } from "@swissfederalrailways/dom-component-path";
import { querySelectorDeep } from "./query-selector";
import {
  PageLoadedParameters,
  DataLayerEntry,
  SearchLoadedParameters,
  TrackAccordionParameters,
  ComponentPath,
  NavigationParameters,
  CustomEventParameters,
  UserLoginTypeParameters,
  TrackSearchResultNavigationParameters,
  TrackDownloadParameters,
  TrackImpressionParameters,
  TrackTeaserNavigationParameters,
  TrackTeaserImpressionParameters,
  TrackCustomNavigationParameters,
  EventParameters,
} from "./types";
import {
  setEventCategory,
  setEventInfo,
  setPageAttributes,
  setUserProfileInfo,
} from "./utils";

export const REDIRECTION_TIMEOUT = 300;

window.digitalDataLayer = window.digitalDataLayer || [];
let callbackIsPending = false;

function pushToDataLayer(entry: DataLayerEntry) {
  if (!callbackIsPending) {
    window.digitalDataLayer.push(entry);
  }
  if (entry.event?.eventInfo?.callback) {
    callbackIsPending = true;
  }
}

const dataLayer = {
  pageLoaded(params: PageLoadedParameters) {
    assertPayload.pageLoaded(params);

    const pageLoadedObj: DataLayerEntry = {
      pageInstanceID: params.pageInstanceID,
      page: {
        pageInfo: {
          pageName: params.pageName,
          destinationURL: window.location.href,
          destinationURI: cleanCampaignQueryParams(params.destinationURI),
          referringURL: params.referrer ?? document.referrer,
          language: params.language,
          pageID: params.pageID,
          sysEnv: params.sysEnv,
        },
        category: {
          primaryCategory: params.primaryCategory,
        },
      },
      user: [
        {
          profile: [
            {
              profileInfo: {
                loginStatus: params.user?.loginStatus ?? 0,
                language:
                  params.user?.loginStatus === 1
                    ? params.user.language
                    : params.language,
                loginType: params.user?.loginType,
              },
            },
          ],
        },
      ],
    };
    if (params.search) {
      pageLoadedObj.search = getSearchDataLayerObject(params.search);
    }
    if (params.errorCode || params.errorMessage) {
      setPageAttributes(pageLoadedObj, {
        errorCode: params.errorCode,
        errorMessage: params.errorMessage,
      });
    }
    if (params.user?.loginStatus === 1) {
      setUserProfileInfo(pageLoadedObj, {
        UUID: params.user.UUID,
        age: params.user.age,
        gender: params.user.gender,
        zipcode: params.user.zipcode,
      });
    }
    pushToDataLayer(pageLoadedObj);
  },

  searchLoaded(params: SearchLoadedParameters) {
    assertPayload.searchLoaded(params);
    pushToDataLayer({ search: getSearchDataLayerObject(params) });
  },

  dataLayerReady() {
    propagateAdditionalPageLoadDetails();
    pushToDataLayer({
      event: {
        eventInfo: {
          eventName: "page data layer ready",
        },
      },
    });
  },

  trackAccordionAction(params: TrackAccordionParameters) {
    assertPayload.accordion(params);

    pushToDataLayer({
      event: {
        eventInfo: {
          eventName: "click",
          eventType: "action",
          destination: "",
          componentPath: convertToComponentPath(params.component),
          label: params.label,
          action: params.action,
          headline: params.headline ?? "",
        },
        category: {
          primaryCategory: "accordion",
        },
      },
    });
  },

  trackGenericNavigation(params: NavigationParameters) {
    assertPayload.genericNavigation(params);
    pushToDataLayer(createGenericNavigationEvent(params));
  },

  trackGenericClick(params: EventParameters) {
    assertPayload.genericClick(params);
    pushToDataLayer({
      event: {
        eventInfo: {
          eventName: "click",
          eventType: "action",
          destination: "",
          componentPath: convertToComponentPath(params.component),
          label: params.label,
          location: params.location,
          variant: params.variant,
          detail: params.detail,
        },
        category: {
          primaryCategory: "generic navigation",
        },
      },
    });
  },

  trackTeaserImpression(params: TrackTeaserImpressionParameters) {
    assertPayload.teaserImpression(params);
    pushToDataLayer({
      event: {
        eventInfo: {
          eventName: "impression",
          eventType: "",
          destination: params.destination,
          componentPath: convertToComponentPath(params.component),
          label: params.label,
          headline: params.headline,
          componentID: getComponentId(params),
        },
        category: {
          primaryCategory: "teaser",
        },
      },
    });
  },

  trackTeaserNavigation(params: TrackTeaserNavigationParameters) {
    assertPayload.teaserNavigation(params);
    const eventObject = createGenericNavigationEvent(params);
    setEventInfo(eventObject, {
      componentID: getComponentId(params),
      headline: params.headline,
    });
    setEventCategory(eventObject, { primaryCategory: "teaser" });
    pushToDataLayer(eventObject);
  },

  trackImpression(params: TrackImpressionParameters) {
    assertPayload.impression(params);
    pushToDataLayer({
      event: {
        eventInfo: {
          eventName: "impression",
          eventType: "",
          componentPath: convertToComponentPath(params.component),
          label: params.label,
          headline: params.headline,
        },
        category: {
          primaryCategory: params.type,
        },
      },
    });
  },

  trackDownload(params: TrackDownloadParameters) {
    assertPayload.download(params);
    const eventObject = createGenericNavigationEvent(params);
    setEventInfo(eventObject, {
      eventType: "download",
      value: params.filename,
    });
    setEventCategory(eventObject, { primaryCategory: "download" });
    pushToDataLayer(eventObject);
  },

  trackSearchResultNavigation(params: TrackSearchResultNavigationParameters) {
    assertPayload.searchResult(params);
    const eventObject = createGenericNavigationEvent(params);
    setEventInfo(eventObject, {
      action: `click on search result${params.type ? ` (${params.type})` : ""}`,
      value: params.value,
      resultPosition: params.position + 1,
      resultPageNumber: params.page ? params.page + 1 : 1,
    });
    setEventCategory(eventObject, {
      primaryCategory: `${params.level} search`,
    });
    pushToDataLayer(eventObject);
  },

  trackCustomNavigation(params: TrackCustomNavigationParameters) {
    assertPayload.customNavigation(params);
    const eventObject = createGenericNavigationEvent(params);
    if (params.primaryCategory !== undefined) {
      setEventCategory(eventObject, {
        primaryCategory: params.primaryCategory,
      });
    }
    if (params.action !== undefined) {
      setEventInfo(eventObject, { action: params.action });
    }
    pushToDataLayer(eventObject);
  },

  updateUserLoginType(params: UserLoginTypeParameters) {
    assertPayload.loginType(params);
    pushToDataLayer({
      user: [
        {
          profile: [
            {
              profileInfo: {
                loginType: params.loginType,
              },
            },
          ],
        },
      ],
    });
    pushToDataLayer({
      event: {
        eventInfo: {
          eventName: "user update",
        },
      },
    });
  },

  sendCustomEvent(params: CustomEventParameters) {
    assertPayload.customEvent(params);
    const { primaryCategory, ...eventInfoProperties } = params;
    pushToDataLayer({
      event: {
        eventInfo: {
          ...eventInfoProperties,
        },
        category: {
          primaryCategory: primaryCategory,
        },
      },
    });
  },
};

export default dataLayer;

function getSearchDataLayerObject(params: {
  level: string;
  value: string;
  results: number;
}) {
  return {
    name: `${params.level} search`,
    term: params.value,
    results: params.results,
    resultPageNumber: params.results === 0 ? 0 : 1,
  };
}

function convertToComponentPath(path: Element | ComponentPath) {
  if (path instanceof Element) {
    return domComponentPath.get(path);
  }
  return path;
}

function getComponentId({
  headline,
  label,
  destination,
}: {
  headline: string;
  label: string;
  destination: string;
}) {
  return `${headline}/${label}/${destination}`;
}

function wrapCallback(callback: () => void) {
  function redirectCallback() {
    clearTimeout(timeoutId);
    callbackIsPending = false;
    callback();
  }

  const timeoutId = setTimeout(redirectCallback, REDIRECTION_TIMEOUT);

  return redirectCallback;
}

function createGenericNavigationEvent({
  callback,
  component,
  label,
  destination,
  location,
  variant,
  detail,
}: NavigationParameters) {
  let eventType = destination === "" ? "action" : "navigation";
  if (destination.startsWith("mailto:")) eventType = "mail";
  if (destination.startsWith("tel:")) eventType = "call";

  const eventObject: DataLayerEntry = {
    event: {
      eventInfo: {
        eventName: "click",
        eventType,
        destination,
        componentPath: convertToComponentPath(component),
        label,
        location,
        variant,
        detail,
      },
      category: {
        primaryCategory: "generic navigation",
      },
    },
  };

  if (eventType === "navigation" && callback) {
    setEventInfo(eventObject, { callback: wrapCallback(callback) });
  }
  return eventObject;
}

function cleanCampaignQueryParams(url: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  const search = anchor.search;

  if (!search) {
    return url;
  }

  const cleanedParams = search
    .slice(1)
    .split("&")
    .filter((param) => !param.startsWith("at_"))
    .join("&");
  const ie11LeadingSlashFix =
    url.startsWith("/") && !anchor.pathname.startsWith("/") ? "/" : "";
  if (!cleanedParams) {
    return `${ie11LeadingSlashFix}${anchor.pathname}`;
  }
  return `${ie11LeadingSlashFix}${anchor.pathname}?${cleanedParams}`;
}

function propagateAdditionalPageLoadDetails() {
  const element = querySelectorDeep("[data-sit-search-loaded]");
  if (element) {
    try {
      const parsedSearchLoaded = JSON.parse(
        element.dataset.sitSearchLoaded ?? "",
      );
      dataLayer.searchLoaded(parsedSearchLoaded);
    } catch (e) {
      console.error("Could not parse given data-sit-search-loaded: ", e);
    }
  }
}
